import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { ChevronRight, Download, Loader2, MoreHorizontal, SlidersHorizontal } from 'lucide-react';
import {
  api,
  type BandaPlan,
  type CitaPlan,
  type EstadoPlan,
  type PlanMejora,
  type PuntoPlan,
} from '@/lib/api';
import { useApi } from '@/hooks/useApi';
import { classNames, toCsv } from '@/lib/utils';
import {
  COLOR_BANDA,
  Chip,
  ETIQUETA_BANDA,
  ETIQUETA_ESTADO,
  EtiquetaCanal,
  PieInfinito,
  fechaCorta,
  useScrollInfinito,
} from '@/components/reviews/shared';
import AuditoriaListing from '@/components/reviews/AuditoriaListing';
import type { CtxResenas } from '@/pages/ReviewsLayout';

/**
 * Plan de mejora: lo que los huéspedes piden arreglar, agrupado por alojamiento.
 *
 * La unidad es el par (alojamiento, tema), no la reseña. Una bandeja de 87
 * reseñas no se vacía nunca; una lista de 18 temas con su recuento sí, y es la
 * única forma de que esto siga abierto la segunda semana.
 *
 * Todo lo que se pulsa aquí escribe de verdad en el servidor y se pinta antes de
 * que conteste, con cinco segundos para deshacer. Marcar veinte puntos con el
 * pulgar sin miedo a un toque accidental es lo que decide que la pantalla se use.
 */

const POR_PAGINA = 30;

/** Da tiempo a leer la franja y rectificar sin dejar la lista congelada. */
const MS_DESHACER = 5000;

/** El tope del servidor para `limite`; más allá el botón deja de crecer. */
const TOPE_SERVIDOR = 400;

const ESTADOS_FILTRO: { estado: EstadoPlan; etiqueta: string }[] = [
  { estado: 'pendiente', etiqueta: 'Pendientes' },
  { estado: 'en_curso', etiqueta: 'En curso' },
  { estado: 'resuelto', etiqueta: 'Resueltos' },
  { estado: 'descartado', etiqueta: 'Descartados' },
];

/** Pendiente y en curso son «abierto»: es lo que queda por hacer. */
const ABIERTOS: EstadoPlan[] = ['pendiente', 'en_curso'];

/**
 * El modo oscuro de la banda crítica, a mano.
 *
 * `bg-amber-100`/`text-amber-800` y `bg-slate-100` sí están en la tabla de
 * overrides de `index.css`, pero el par rosa no: sin esto, la etiqueta más
 * importante de la pantalla queda rosa claro sobre rosa claro.
 */
const OSCURO_BANDA: Record<BandaPlan, string> = {
  critico: 'dark:bg-rose-500/25 dark:text-rose-200',
  revisar: '',
  menor: '',
};

const TEXTO_MARCA: Record<EstadoPlan, string> = {
  pendiente: 'Devuelto a pendiente',
  en_curso: 'Marcado como en curso',
  resuelto: 'Marcado como resuelto',
  descartado: 'Tema descartado',
};

/** Cambio hecho en el móvil que el servidor ya conoce pero la lista aún no. */
interface CambioLocal {
  punto: PuntoPlan;
  antes: EstadoPlan;
  ahora: EstadoPlan;
}

interface Grupo {
  listingId: string;
  nombre: string;
  zona: string | null;
  puntos: PuntoPlan[];
}

function sinClave<T>(mapa: Record<string, T>, clave: string): Record<string, T> {
  const copia = { ...mapa };
  delete copia[clave];
  return copia;
}

function mensaje(e: unknown, porDefecto: string): string {
  return e instanceof Error && e.message ? e.message : porDefecto;
}

function plural(n: number, singular: string, plural_: string): string {
  return `${n.toLocaleString('es-ES')} ${n === 1 ? singular : plural_}`;
}

/**
 * El texto de una mención, o `null` si no tiene.
 *
 * Hay reseñas que solo traen nota, y hay temas que salen de una sub-nota floja:
 * en los dos casos `texto` y `fragmentos` llegan vacíos, y un párrafo en blanco
 * donde se espera una cita se lee como que la pantalla se ha roto.
 */
function cuerpo(c: CitaPlan): string | null {
  if (c.texto && c.texto.trim()) return c.texto;
  const unidos = c.fragmentos.filter((f) => f.trim()).join(' … ');
  return unidos || null;
}

/**
 * Agrupa por alojamiento respetando el orden en que llegan.
 *
 * El servidor manda los puntos por prioridad descendente, así que el primer
 * punto de cada piso es también el más grave que tiene: agrupar por primera
 * aparición ordena los grupos por su prioridad máxima sin volver a ordenar nada.
 */
function agrupa(puntos: PuntoPlan[]): Grupo[] {
  const grupos = new Map<string, Grupo>();
  for (const p of puntos) {
    const g = grupos.get(p.listingId);
    if (g) g.puntos.push(p);
    else grupos.set(p.listingId, { listingId: p.listingId, nombre: p.nombre, zona: p.zona, puntos: [p] });
  }
  return [...grupos.values()];
}

export default function ReviewsPlanPage() {
  const { rango } = useOutletContext<CtxResenas>();

  const [estados, setEstados] = useState<EstadoPlan[]>(ABIERTOS);
  const [temas, setTemas] = useState<string[]>([]);
  // Una sola zona a la vez y no multiselección: lo que se pidió es ver Alicante
  // por un lado y Barbate por otro, y con varias marcadas se vuelve a mezclar
  // justo lo que se quería separar. «Todas» es el valor vacío.
  const [zona, setZona] = useState<string>('');
  const [paginas, setPaginas] = useState(1);

  // Se pide siempre desde el principio con un límite mayor en vez de acumular
  // páginas sueltas en el cliente: con filtros que cambian el orden, dos trozos
  // pedidos en momentos distintos se solapan y repiten puntos.
  const limite = Math.min(TOPE_SERVIDOR, paginas * POR_PAGINA);

  const { data: plan, loading, error, refresh } = useApi<PlanMejora>(
    () =>
      api.getPlanMejora({
        rango,
        estado: estados.join(','),
        limite: String(limite),
        ...(temas.length ? { tema: temas.join(',') } : {}),
        ...(zona ? { zona } : {}),
      }),
    [rango, estados.join(','), temas.join(','), zona, limite],
  );

  const [cambios, setCambios] = useState<Record<string, CambioLocal>>({});
  const [franjas, setFranjas] = useState<Record<string, EstadoPlan>>({});
  const [descartes, setDescartes] = useState<Record<string, Record<string, boolean>>>({});
  const [errores, setErrores] = useState<Record<string, string>>({});
  const [citasAbiertas, setCitasAbiertas] = useState<Record<string, boolean>>({});
  const [panel, setPanel] = useState<string | null>(null);
  const [notas, setNotas] = useState<Record<string, string>>({});
  const [notasGuardadas, setNotasGuardadas] = useState<Record<string, boolean>>({});
  const [guardandoNota, setGuardandoNota] = useState<string | null>(null);

  const relojes = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const escrituras = useRef(new Map<string, Promise<unknown>>());

  // Sin esto, una franja abierta al salir de la pantalla intentaría escribir
  // estado en un componente que ya no existe.
  useEffect(() => {
    const abiertos = relojes.current;
    return () => {
      abiertos.forEach((t) => clearTimeout(t));
      abiertos.clear();
    };
  }, []);

  const estadoDe = (p: PuntoPlan): EstadoPlan => cambios[p.id]?.ahora ?? p.estado;

  const estaDescartada = (p: PuntoPlan, c: CitaPlan): boolean =>
    descartes[p.id]?.[c.reviewId] ?? c.descartada;

  /**
   * La mención que se cita en la tarjeta.
   *
   * Manda `destacada`, que el servidor elige como la más reciente cuyo texto
   * habla DE ESTE TEMA. La más reciente a secas no vale: la mayoría de los temas
   * se deducen también de una sub-nota floja, y un piso con una reseña muy mala
   * acababa con tres tarjetas —Camas, Equipamiento y Comunicación— citando
   * todas la misma primera frase del mismo texto.
   *
   * Si el gestor descarta a mano la destacada, se cae a la siguiente que siga
   * contando; y si el tema solo sale de las notas, no hay cita y se dice.
   */
  const citaPrincipal = (p: PuntoPlan): CitaPlan | null => {
    if (p.destacada) {
      const suya = p.citas.find((c) => c.reviewId === p.destacada!.reviewId);
      if (suya && !estaDescartada(p, suya)) return suya;
    }
    return p.citas.find((c) => !estaDescartada(p, c) && c.fragmentos.length > 0) ?? null;
  };

  /**
   * Encadena las escrituras de un mismo punto.
   *
   * Si se pulsa «Deshacer» con el PUT anterior todavía en vuelo, el servidor
   * tiene que recibirlos en el orden en que se pulsaron y no en el que
   * respondan: al revés, el punto se quedaría guardado como resuelto.
   */
  function escribeEstado(p: PuntoPlan, estado: EstadoPlan, alFallar: (texto: string) => void) {
    const previa = escrituras.current.get(p.id) ?? Promise.resolve();
    const tarea = previa
      .catch(() => undefined)
      .then(() => api.guardarPuntoPlan(p.listingId, p.tema, { estado }))
      .catch((e) => alFallar(mensaje(e, 'No se pudo guardar el cambio')));
    escrituras.current.set(p.id, tarea);
  }

  function cierraFranja(id: string) {
    const reloj = relojes.current.get(id);
    if (reloj) clearTimeout(reloj);
    relojes.current.delete(id);
    setFranjas((f) => sinClave(f, id));
  }

  function marcar(p: PuntoPlan, destino: EstadoPlan) {
    const antes = estadoDe(p);
    // Volver a pulsar el estado que ya tiene lo devuelve a pendiente: es el
    // único camino de vuelta cuando la franja de deshacer ya ha caducado.
    const ahora: EstadoPlan = antes === destino ? 'pendiente' : destino;

    setErrores((e) => sinClave(e, p.id));
    setCambios((c) => ({ ...c, [p.id]: { punto: p, antes, ahora } }));
    setFranjas((f) => ({ ...f, [p.id]: ahora }));
    setPanel(null);

    const reloj = setTimeout(() => cierraFranja(p.id), MS_DESHACER);
    relojes.current.set(p.id, reloj);

    escribeEstado(p, ahora, (texto) => {
      cierraFranja(p.id);
      setCambios((c) => sinClave(c, p.id));
      setErrores((e) => ({ ...e, [p.id]: texto }));
    });
  }

  function deshacer(id: string) {
    const cambio = cambios[id];
    if (!cambio) return;
    cierraFranja(id);
    setCambios((c) => sinClave(c, id));
    escribeEstado(cambio.punto, cambio.antes, (texto) => {
      // El servidor se quedó con el estado nuevo: se repone el cambio para que
      // la tarjeta no diga una cosa y la base de datos otra.
      setCambios((c) => ({ ...c, [id]: cambio }));
      setErrores((e) => ({ ...e, [id]: texto }));
    });
  }

  async function cambiaDescarte(p: PuntoPlan, c: CitaPlan, descartar: boolean) {
    const previo = estaDescartada(p, c);
    setErrores((e) => sinClave(e, p.id));
    setDescartes((d) => ({ ...d, [p.id]: { ...(d[p.id] ?? {}), [c.reviewId]: descartar } }));
    try {
      if (descartar) await api.descartarMencion(p.listingId, p.tema, c.reviewId);
      else await api.recuperarMencion(p.listingId, p.tema, c.reviewId);
    } catch (e) {
      setDescartes((d) => ({ ...d, [p.id]: { ...(d[p.id] ?? {}), [c.reviewId]: previo } }));
      setErrores((err) => ({ ...err, [p.id]: mensaje(e, 'No se pudo cambiar la mención') }));
    }
  }

  async function guardaNota(p: PuntoPlan, texto: string) {
    setGuardandoNota(p.id);
    setErrores((e) => sinClave(e, p.id));
    try {
      await api.guardarPuntoPlan(p.listingId, p.tema, { nota: texto.trim() || null });
      setNotasGuardadas((n) => ({ ...n, [p.id]: true }));
    } catch (e) {
      setErrores((err) => ({ ...err, [p.id]: mensaje(e, 'No se pudo guardar la nota') }));
    } finally {
      setGuardandoNota(null);
    }
  }

  async function quitaMarca(p: PuntoPlan) {
    setErrores((e) => sinClave(e, p.id));
    try {
      await api.borrarPuntoPlan(p.listingId, p.tema);
      cierraFranja(p.id);
      setCambios((c) => sinClave(c, p.id));
      setNotas((n) => sinClave(n, p.id));
      setNotasGuardadas((n) => sinClave(n, p.id));
      setPanel(null);
      // El punto vuelve a nacer de las reseñas, así que se relee: su estado, su
      // nota y su reincidencia ya no son los que hay en pantalla.
      refresh();
    } catch (e) {
      setErrores((err) => ({ ...err, [p.id]: mensaje(e, 'No se pudo quitar la marca') }));
    }
  }

  function alternaEstado(estado: EstadoPlan) {
    setPaginas(1);
    setEstados((actuales) => {
      // Nunca se queda vacío: sin ningún estado marcado el servidor volvería a
      // los abiertos por defecto y la pantalla enseñaría algo que el filtro niega.
      if (actuales.includes(estado)) {
        return actuales.length === 1 ? actuales : actuales.filter((e) => e !== estado);
      }
      return [...actuales, estado];
    });
  }

  function alternaTema(tema: string) {
    setPaginas(1);
    setTemas((actuales) =>
      actuales.includes(tema) ? actuales.filter((t) => t !== tema) : [...actuales, tema],
    );
  }

  // Con la franja abierta el punto sigue ocupando su hueco aunque su estado
  // nuevo ya no pase el filtro: si desapareciera, no habría dónde pulsar deshacer.
  const visibles = useMemo(
    () => (plan?.items ?? []).filter((p) => franjas[p.id] || estados.includes(estadoDe(p))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [plan, franjas, cambios, estados],
  );

  const grupos = useMemo(() => agrupa(visibles), [visibles]);

  /** Los rótulos del filtro, corregidos con lo que se acaba de pulsar. */
  const contadores = useMemo(() => {
    // Los cuatro estados se siembran a cero antes de mezclar lo que llega: un
    // estado sin ningún punto puede no venir en la respuesta, y entonces el
    // rótulo del filtro diría «NaN» en cuanto se pulsara algo.
    const base: Record<EstadoPlan, number> = {
      pendiente: 0,
      en_curso: 0,
      resuelto: 0,
      descartado: 0,
      ...(plan?.contadores ?? {}),
    };
    for (const c of Object.values(cambios)) {
      base[c.antes] = Math.max(0, base[c.antes] - 1);
      base[c.ahora] += 1;
    }
    return base;
  }, [plan, cambios]);

  const resumen = useMemo(() => {
    const locales = Object.values(cambios);
    const delta = (predicado: (c: CambioLocal, estado: EstadoPlan) => boolean) =>
      locales.reduce(
        (suma, c) => suma + (predicado(c, c.ahora) ? 1 : 0) - (predicado(c, c.antes) ? 1 : 0),
        0,
      );
    return {
      abiertos: Math.max(0, (plan?.abiertos ?? 0) + delta((_, e) => ABIERTOS.includes(e))),
      criticos: Math.max(
        0,
        (plan?.criticos ?? 0) + delta((c, e) => ABIERTOS.includes(e) && c.punto.banda === 'critico'),
      ),
      pisos: plan?.pisos ?? 0,
    };
  }, [plan, cambios]);

  function exportar() {
    if (!plan) return;
    const filas: (string | number)[][] = [
      ['zona', 'alojamiento', 'tema', 'estado', 'banda', 'prioridad', 'menciones', 'primera', 'ultima', 'ultima_cita'],
      ...plan.items.map((p) => {
        const cita = citaPrincipal(p);
        return [
          p.zona ?? '',
          p.nombre,
          p.temaEtiqueta,
          ETIQUETA_ESTADO[estadoDe(p)],
          ETIQUETA_BANDA[p.banda],
          p.prioridad,
          p.menciones,
          p.primera ?? '',
          p.ultima ?? '',
          cita?.fragmentos[0] ?? cita?.texto ?? '',
        ];
      }),
    ];
    const url = URL.createObjectURL(new Blob([toCsv(filas)], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `plan-mejora-${rango}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const propiedadesTarjeta = (p: PuntoPlan) => ({
    punto: p,
    estado: estadoDe(p),
    cita: citaPrincipal(p),
    citaAbierta: !!citasAbiertas[p.id],
    panelAbierto: panel === p.id,
    error: errores[p.id],
    nota: notas[p.id] ?? p.nota ?? '',
    notaGuardada: !!notasGuardadas[p.id],
    guardandoNota: guardandoNota === p.id,
    esDescartada: (c: CitaPlan) => estaDescartada(p, c),
    onAbrirCitas: () => setCitasAbiertas((c) => ({ ...c, [p.id]: !c[p.id] })),
    onAbrirPanel: () => setPanel((actual) => (actual === p.id ? null : p.id)),
    onMarcar: (destino: EstadoPlan) => marcar(p, destino),
    onDescarte: (c: CitaPlan, descartar: boolean) => cambiaDescarte(p, c, descartar),
    onNota: (texto: string) => {
      setNotas((n) => ({ ...n, [p.id]: texto }));
      setNotasGuardadas((n) => sinClave(n, p.id));
    },
    onGuardarNota: () => guardaNota(p, notas[p.id] ?? p.nota ?? ''),
    onQuitarMarca: () => quitaMarca(p),
  });

  // Se pide otra página al acercarse el final de la lista. `plan` puede no
  // existir todavía en el primer render, de ahí las comprobaciones dentro.
  const centinela = useScrollInfinito(() => setPaginas((n) => n + 1), {
    activo: !!plan && plan.items.length < plan.total && limite < TOPE_SERVIDOR,
    cargando: loading,
  });

  /** Al cambiar de zona se vuelve a la primera página: la lista es otra. */
  function elegirZona(nueva: string) {
    setZona((actual) => (actual === nueva ? '' : nueva));
    setPaginas(1);
  }

  if (loading && !plan) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="animate-spin text-slate-400" size={28} />
      </div>
    );
  }

  if (error && !plan) {
    return <div className="card py-3 text-sm text-amber-700 dark:text-amber-300">{error}</div>;
  }

  if (!plan) return null;

  const quedanPorCargar = plan.items.length < plan.total && limite < TOPE_SERVIDOR;

  // Con la lista vacía hay que decir si no hay trabajo pendiente o si son los
  // filtros los que lo esconden: son dos noticias distintas.
  const hayFiltros =
    temas.length > 0 ||
    zona !== '' ||
    estados.length !== ABIERTOS.length ||
    !ABIERTOS.every((e) => estados.includes(e));

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <p className="text-[13px] text-slate-500">
          {plural(resumen.abiertos, 'punto abierto', 'puntos abiertos')} en{' '}
          {plural(resumen.pisos, 'alojamiento', 'alojamientos')}
          {resumen.criticos > 0 && <> · {resumen.criticos.toLocaleString('es-ES')} críticos</>}
        </p>
        {/* El CSV solo en escritorio: en el móvil la descarga acaba en una
            carpeta que nadie abre, y aquí es un atajo para pasar el plan a quien
            hace la obra. */}
        <button
          type="button"
          onClick={exportar}
          className="btn-secondary hidden lg:inline-flex items-center gap-1.5 min-h-11 py-0 text-sm shrink-0"
        >
          <Download size={15} /> Exportar CSV
        </button>
      </div>

      {/* Las zonas, en una fila propia y por encima del estado.
          Es lo primero que se elige —«hoy miro Alicante»— y por eso va arriba;
          scroll horizontal aquí y no `flex-wrap` porque son seis y una fila que
          crece a tres empujaría la lista fuera de la pantalla en un móvil. Los
          recuentos vienen del servidor SIN el filtro de zona puesto, así que
          siguen diciendo qué hay en las demás mientras estás dentro de una. */}
      {plan.zonas.length > 1 && (
        <div
          className="flex gap-1.5 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-none"
          role="group"
          aria-label="Filtrar por zona"
        >
          {/* El recuento de «Todas» se suma de las zonas y no se toma del
              resumen: el resumen ya viene filtrado, así que dentro de Zahara
              decía «Todas 35» y parecía que el catálogo entero eran 35 puntos. */}
          <Chip
            activo={zona === ''}
            onClick={() => elegirZona('')}
            count={plan.zonas.reduce((n, z) => n + z.abiertos, 0)}
          >
            Todas
          </Chip>
          {plan.zonas.map((z) => (
            <Chip
              key={z.zona}
              activo={zona === z.zona}
              onClick={() => elegirZona(z.zona)}
              count={z.abiertos}
            >
              {z.zona}
            </Chip>
          ))}
        </div>
      )}

      {/* El segmentado ES el filtro: no hay un control aparte que haya que
          entender antes de tocar nada. Multiselección porque «abierto» son dos
          estados y se miran juntos. */}
      <div
        className="sticky top-0 z-20 h-12 -mx-4 px-4 sm:mx-0 sm:px-0 bg-sereno-white flex items-center gap-1"
        role="group"
        aria-label="Filtrar por estado"
      >
        {ESTADOS_FILTRO.map(({ estado, etiqueta }) => {
          const activo = estados.includes(estado);
          return (
            <button
              key={estado}
              type="button"
              onClick={() => alternaEstado(estado)}
              aria-pressed={activo}
              className={classNames(
                'flex-1 min-h-11 px-1 rounded-md text-[13px] leading-tight transition-colors',
                activo
                  ? 'bg-sereno-dark text-white an-active'
                  : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5',
              )}
            >
              <span className="block truncate">{etiqueta}</span>
              <span className={classNames('block text-[11px] tabular-nums', activo ? 'opacity-80' : 'text-slate-400')}>
                {contadores[estado].toLocaleString('es-ES')}
              </span>
            </button>
          );
        })}
      </div>

      <details className="rounded-lg border border-slate-200 dark:border-white/10">
        <summary className="min-h-11 py-3 px-3 text-sm cursor-pointer select-none marker:text-slate-400">
          <span className="inline-flex items-center gap-2 align-middle">
            <SlidersHorizontal size={15} className="text-slate-400" />
            Filtrar por tema
            {temas.length > 0 && (
              <span className="badge bg-slate-100 text-slate-600">
                {temas.length.toLocaleString('es-ES')}
              </span>
            )}
          </span>
        </summary>
        {/* En una sola tira con desplazamiento lateral la mitad de los temas
            quedan fuera de pantalla y nadie los busca: envuelven. */}
        <div className="px-3 pb-3 flex flex-wrap gap-2">
          {!plan.temas.length && (
            <p className="text-sm text-slate-500 py-2">
              Ninguna reseña de este periodo menciona un tema clasificable.
            </p>
          )}
          {plan.temas.map((t) => (
            <Chip
              key={t.tema}
              activo={temas.includes(t.tema)}
              count={t.abiertos}
              onClick={() => alternaTema(t.tema)}
            >
              {t.etiqueta}
            </Chip>
          ))}
          {temas.length > 0 && (
            <button
              type="button"
              onClick={() => { setTemas([]); setPaginas(1); }}
              className="min-h-11 px-3 rounded-md text-sm text-slate-500 hover:bg-slate-100 dark:hover:bg-white/5"
            >
              Quitar filtros
            </button>
          )}
        </div>
      </details>

      {error && <div className="card py-3 text-sm text-amber-700 dark:text-amber-300">{error}</div>}

      {!grupos.length ? (
        <div className="card text-center py-12 space-y-3">
          <p className="text-slate-500">
            {hayFiltros
              ? 'Ningún tema encaja con los filtros puestos.'
              : plan.total === 0 && plan.noAccionables.length === 0
                ? 'Las reseñas de este periodo no piden arreglar nada. Prueba con una ventana más larga.'
                : 'No queda ningún tema abierto en este periodo.'}
          </p>
          {hayFiltros && (
            <button
              type="button"
              onClick={() => { setTemas([]); setEstados(ABIERTOS); setPaginas(1); }}
              className="btn-secondary min-h-11 py-0 text-sm"
            >
              Ver los abiertos de todos los temas
            </button>
          )}
        </div>
      ) : (
        grupos.map((g) => (
          <section key={g.listingId}>
            {/* El nombre del alojamiento es el título de su bloque, no un dato
                más de una línea de 12 px: al bajar por el plan lo que sitúa es
                de qué casa se está hablando, y con todo al mismo tamaño había
                que pararse a leer para saberlo. La zona sube encima, pequeña,
                porque acompaña al nombre pero no compite con él. */}
            <Link
              to={`/reviews/l/${g.listingId}`}
              className="sticky top-12 z-10 -mx-4 px-4 sm:mx-0 sm:px-2 py-2 flex items-start gap-2 bg-sereno-white border-b border-slate-200 dark:border-white/10"
            >
              <span className="min-w-0 flex-1">
                {g.zona && (
                  <span className="block text-[11px] uppercase tracking-wide text-slate-400 truncate">
                    {g.zona}
                  </span>
                )}
                <span className="block text-base sm:text-lg font-bold leading-tight truncate">
                  {g.nombre}
                </span>
              </span>
              <span className="shrink-0 text-[12px] text-slate-500 whitespace-nowrap mt-0.5">
                {plural(g.puntos.length, 'tema', 'temas')}
              </span>
              <ChevronRight size={16} className="shrink-0 text-slate-400 mt-0.5" />
            </Link>

            <div className="mt-2">
              <AuditoriaListing listingId={g.listingId} nombre={g.nombre} rango={rango} />
            </div>

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {g.puntos.map((p) =>
                franjas[p.id] ? (
                  <FranjaDeshacer key={p.id} estado={franjas[p.id]} onDeshacer={() => deshacer(p.id)} />
                ) : (
                  <TarjetaPunto key={p.id} accionable {...propiedadesTarjeta(p)} />
                ),
              )}
            </div>
          </section>
        ))
      )}

      <PieInfinito
        centinela={centinela}
        cargando={loading && quedanPorCargar}
        error={quedanPorCargar ? error : null}
        onReintentar={refresh}
        mostrarAgotado={!quedanPorCargar && plan.items.length > 0}
        agotadoTexto={
          plan.items.length >= plan.total
            ? `${plan.items.length.toLocaleString('es-ES')} puntos, no hay más`
            : `Se muestran los ${plan.items.length.toLocaleString('es-ES')} más prioritarios de ${plan.total.toLocaleString('es-ES')}. Filtra por zona o por tema para ver el resto.`
        }
      />

      {plan.noAccionables.length > 0 && (
        // Ubicación y precio informan, no se arreglan: van plegados y sin
        // botones de estado para que no compitan con lo que sí se puede hacer.
        <details className="rounded-xl border border-slate-200 dark:border-white/10">
          <summary className="min-h-11 py-3 px-3 text-sm cursor-pointer select-none marker:text-slate-400">
            Comercial y no accionable (ubicación, precio) ·{' '}
            {plan.noAccionables.length.toLocaleString('es-ES')}
          </summary>
          <div className="px-3 pb-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {plan.noAccionables.map((p) => (
              <TarjetaPunto key={p.id} accionable={false} {...propiedadesTarjeta(p)} />
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

/**
 * El hueco del punto mientras se puede deshacer.
 *
 * Ocupa el sitio de la tarjeta en vez de aparecer abajo como un aviso flotante:
 * así el dedo sabe dónde está lo que acaba de tocar y la lista no da un salto.
 */
function FranjaDeshacer({ estado, onDeshacer }: { estado: EstadoPlan; onDeshacer: () => void }) {
  return (
    <div
      role="status"
      className="min-h-[52px] rounded-lg bg-slate-100 flex items-center justify-between gap-2 px-3 py-1 text-sm"
    >
      <span className="truncate">{TEXTO_MARCA[estado]}</span>
      <button
        type="button"
        onClick={onDeshacer}
        className="h-11 px-4 shrink-0 rounded-lg bg-white border border-slate-200 dark:border-white/10 text-sm hover:bg-slate-50 dark:hover:bg-white/5"
      >
        Deshacer
      </button>
    </div>
  );
}

interface PropsTarjeta {
  punto: PuntoPlan;
  estado: EstadoPlan;
  cita: CitaPlan | null;
  accionable: boolean;
  citaAbierta: boolean;
  panelAbierto: boolean;
  error?: string;
  nota: string;
  notaGuardada: boolean;
  guardandoNota: boolean;
  esDescartada: (c: CitaPlan) => boolean;
  onAbrirCitas: () => void;
  onAbrirPanel: () => void;
  onMarcar: (destino: EstadoPlan) => void;
  onDescarte: (c: CitaPlan, descartar: boolean) => void;
  onNota: (texto: string) => void;
  onGuardarNota: () => void;
  onQuitarMarca: () => void;
}

function TarjetaPunto({
  punto: p,
  estado,
  cita,
  accionable,
  citaAbierta,
  panelAbierto,
  error,
  nota,
  notaGuardada,
  guardandoNota,
  esDescartada,
  onAbrirCitas,
  onAbrirPanel,
  onMarcar,
  onDescarte,
  onNota,
  onGuardarNota,
  onQuitarMarca,
}: PropsTarjeta) {
  const fragmento = cita?.fragmentos[0] ?? null;

  // «Ver 0 menciones» sería mentira en los temas que solo salen de las sub-notas,
  // y esconder el recorte del servidor haría creer que faltan menciones.
  let etiquetaCitas: string;
  if (citaAbierta) etiquetaCitas = 'Ocultar el detalle';
  else if (!p.citas.length) etiquetaCitas = 'Ver de dónde sale';
  else {
    etiquetaCitas = `Ver ${plural(p.citas.length, 'mención', 'menciones')}`;
    if (p.citasTotales > p.citas.length) {
      etiquetaCitas += ` de ${p.citasTotales.toLocaleString('es-ES')}`;
    }
  }

  return (
    <div className="card p-3 space-y-2 min-h-[112px]">
      <div className="flex flex-wrap items-center gap-1.5">
        {/* La banda va escrita dentro de la etiqueta y no solo en su color: rosa
            y ámbar son el mismo tono para un daltónico, y el color tampoco
            sobrevive a una captura en blanco y negro pegada en un grupo. La
            cifra de prioridad no se pinta —es el orden de la lista, y en una
            tarjeta de 375 px sería un número sin unidad—; va en el CSV. */}
        <span className={classNames('badge', COLOR_BANDA[p.banda], OSCURO_BANDA[p.banda])}>
          {p.temaEtiqueta} · {ETIQUETA_BANDA[p.banda]}
        </span>
        <span className="badge bg-slate-100 text-slate-600">
          {plural(p.menciones, 'mención', 'menciones')}
          {p.menciones90d > 0 && ` · ${p.menciones90d.toLocaleString('es-ES')} en 90 días`}
        </span>
        {p.recaida && (
          <span className={classNames('badge', COLOR_BANDA.critico, OSCURO_BANDA.critico)}>
            Reaparece tras resolverse · {(p.reincidencia + 1).toLocaleString('es-ES')}.ª vez
          </span>
        )}
        {estado === 'descartado' && (
          <span className="badge bg-slate-100 text-slate-600">{ETIQUETA_ESTADO.descartado}</span>
        )}
      </div>

      {/* Tocar la cita abre las menciones aquí mismo. Un modal sobre un texto de
          longitud desconocida en 375 px tapa la lista y obliga a cerrarlo para
          seguir; esto empuja hacia abajo y se cierra donde se abrió.
          El botón se pinta aunque no haya cita: hay temas que salen solo de las
          sub-notas —nadie escribió «el sofá es incómodo», pero puntuaron el
          confort con un 2,5/10— y hay que poder abrir las menciones igual. */}
      <button
        type="button"
        onClick={onAbrirCitas}
        aria-expanded={citaAbierta}
        className="block w-full text-left min-h-11 py-1"
      >
        {fragmento ? (
          <span className="block text-[13px] italic text-slate-600 dark:text-slate-300 line-clamp-2 break-words">
            «{fragmento}»
          </span>
        ) : (
          <span className="block text-[13px] text-slate-500">
            Nadie lo escribió: sale de las notas que pusieron los huéspedes.
          </span>
        )}
        <span className="block text-[11px] text-slate-500 mt-0.5">{etiquetaCitas}</span>
      </button>

      {citaAbierta && (
        <ul className="space-y-2">
          {p.citas.map((c) => {
            const descartada = esDescartada(c);
            const texto = cuerpo(c);
            return (
              <li key={c.reviewId} className="pt-2 border-t border-slate-200 dark:border-white/10">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-slate-500">
                  {c.fecha && <span>{fechaCorta(c.fecha)}</span>}
                  {c.fecha && <span aria-hidden="true" className="text-slate-300 dark:text-slate-600">·</span>}
                  <EtiquetaCanal
                    canal={c.canal}
                    nota5={c.nota5}
                    nativa={c.notaNativa}
                    escala={c.escala}
                  />
                  <ChipIdioma cita={c} />
                </div>
                {texto ? (
                  <p
                    className={classNames(
                      'text-[13px] mt-1 whitespace-pre-line break-words',
                      descartada ? 'line-through text-slate-400' : 'text-slate-600 dark:text-slate-300',
                    )}
                  >
                    {texto}
                  </p>
                ) : (
                  <p className="text-[13px] mt-1 text-slate-500">
                    Reseña sin texto: cuenta por la nota que puso.
                  </p>
                )}
                <button
                  type="button"
                  onClick={() => onDescarte(c, !descartada)}
                  className="mt-1 min-h-11 px-3 rounded-lg border border-slate-200 dark:border-white/10 text-[13px] text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5"
                >
                  {descartada ? 'Recuperar' : 'Descartar mención'}
                </button>
              </li>
            );
          })}
          {!p.citas.length && (
            <li className="pt-2 text-[13px] text-slate-500">
              Este tema sale de las notas: ninguna reseña lo escribió con palabras.
            </li>
          )}
          {p.citasTotales > p.citas.length && (
            // El servidor recorta a las diez más recientes: decirlo evita que se
            // dé por descartado lo que simplemente no ha llegado.
            <li className="pt-2 text-[11px] text-slate-500">
              Se enseñan las {plural(p.citas.length, 'mención más reciente', 'menciones más recientes')} de{' '}
              {p.citasTotales.toLocaleString('es-ES')}.
            </li>
          )}
        </ul>
      )}

      <p className="text-[11px] text-slate-500 flex flex-wrap items-center gap-x-1.5">
        <span>última: {p.ultima ? fechaCorta(p.ultima) : '—'}</span>
        {cita && (
          <>
            <span aria-hidden="true" className="text-slate-300 dark:text-slate-600">·</span>
            <EtiquetaCanal
              canal={cita.canal}
              nota5={cita.nota5}
              nativa={cita.notaNativa}
              escala={cita.escala}
            />
          </>
        )}
      </p>

      {accionable && (
        <>
          <div className="grid grid-cols-[1fr_1fr_44px] gap-2">
            <BotonEstado activo={estado === 'en_curso'} onClick={() => onMarcar('en_curso')}>
              En curso
            </BotonEstado>
            <BotonEstado activo={estado === 'resuelto'} onClick={() => onMarcar('resuelto')}>
              Resuelto
            </BotonEstado>
            <button
              type="button"
              onClick={onAbrirPanel}
              aria-expanded={panelAbierto}
              aria-label="Más acciones sobre este tema"
              className="h-11 rounded-lg border border-slate-200 dark:border-white/10 flex items-center justify-center text-slate-500 hover:bg-slate-100 dark:hover:bg-white/5"
            >
              <MoreHorizontal size={18} />
            </button>
          </div>
          {(estado === 'en_curso' || estado === 'resuelto') && (
            // El camino de vuelta se escribe: es el mismo botón, y sin decirlo
            // solo se descubre pulsando algo que parece que ya está hecho.
            <p className="text-[11px] text-slate-500">
              Vuelve a pulsar «{ETIQUETA_ESTADO[estado]}» para devolverlo a pendiente.
            </p>
          )}
        </>
      )}

      {accionable && panelAbierto && (
        <div className="rounded-lg border border-slate-200 dark:border-white/10 p-3 space-y-2">
          <label className="label" htmlFor={`nota-${p.id}`}>
            Nota interna
          </label>
          <textarea
            id={`nota-${p.id}`}
            rows={3}
            maxLength={500}
            value={nota}
            onChange={(e) => onNota(e.target.value)}
            placeholder="Qué se ha hecho, con quién y cuándo"
            className="input text-sm"
          />
          <div className="flex items-center justify-between text-[11px] text-slate-500">
            <span className="tabular-nums">{nota.length.toLocaleString('es-ES')}/500</span>
            {notaGuardada && <span className="text-emerald-600">Nota guardada</span>}
          </div>
          <button
            type="button"
            onClick={onGuardarNota}
            disabled={guardandoNota}
            className="btn-primary w-full h-11 flex items-center justify-center gap-2 py-0"
          >
            {guardandoNota && <Loader2 className="animate-spin" size={16} />}
            Guardar nota
          </button>
          {/* Lo que hace cada botón va escrito debajo y no en un `title`: este
              panel se usa con el pulgar y en un móvil no hay hover que lo
              enseñe. Son las dos acciones que no se pueden probar «a ver qué
              pasa», así que se explican antes de pulsarlas. */}
          <button
            type="button"
            onClick={() => onMarcar('descartado')}
            className="w-full h-11 rounded-lg border border-slate-200 dark:border-white/10 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5"
          >
            Descartar tema
          </button>
          <p className="text-[11px] text-slate-500">
            El tema no aplica a este piso: se clasificó mal a partir de las palabras de la reseña.
          </p>
          <button
            type="button"
            onClick={onQuitarMarca}
            disabled={!p.actualizadoEn}
            className="w-full h-11 rounded-lg border border-slate-200 dark:border-white/10 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5 disabled:opacity-40"
          >
            Quitar marca
          </button>
          <p className="text-[11px] text-slate-500">
            {p.actualizadoEn
              ? 'Borra el estado, la nota y el historial: el tema vuelve a pendiente.'
              : 'Este tema todavía no tiene ninguna marca guardada.'}
          </p>
          <Link
            to={`/reviews/l/${p.listingId}`}
            className="h-11 flex items-center justify-between text-sm text-slate-600 dark:text-slate-300"
          >
            Ver el alojamiento
            <ChevronRight size={16} className="text-slate-400" />
          </Link>
        </div>
      )}

      {error && <p className="text-[11px] text-rose-600">{error}</p>}
    </div>
  );
}

function BotonEstado({
  activo,
  onClick,
  children,
}: {
  activo: boolean;
  onClick: () => void;
  children: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={activo}
      className={classNames(
        'h-11 rounded-lg text-sm transition-colors',
        activo
          ? 'bg-sereno-dark text-white an-active'
          : 'border border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5',
      )}
    >
      {children}
    </button>
  );
}

/**
 * Idioma de la mención, escrito como texto y no como bandera dentro de un
 * tooltip: en un móvil no hay hover y el dato quedaría inaccesible.
 */
function ChipIdioma({ cita }: { cita: CitaPlan }) {
  if (!cita.idioma || cita.idioma.toLowerCase() === 'es') return null;
  const idioma = cita.idioma.toUpperCase();
  return (
    <span className="badge bg-slate-100 text-slate-600 text-[10px]">
      {cita.textoOriginal ? `Traducido · ${idioma}` : `Sin traducir · ${idioma}`}
    </span>
  );
}
